import get from 'lodash/get'
import {call, put, select, takeLatest} from 'redux-saga/effects'

import {BAD_REQUEST, isBadResponse} from '../../api/statusCodes'

import * as addToLookActions from '../../actions/addToLook'
import * as apiActions from '../../actions/api'
import * as eventAndLooksApi from '../../api/eventAndLooks'
import * as addToLookSelectors from '../../store/addToLook'
import * as eventsSelectors from '../../store/events'
import * as looksTrack from '../../track/looks'
import * as addToLookTrack from '../../track/addToLook'
import * as userInteractionsActions from '../../actions/userInteractions'

import {ATL_ORIGIN_HTO, ATL_ORIGIN_PDP} from '../../const'

/**
 * Handles the tracking of the error modals on the PDP
 *
 * @generator
 */
export function* trackErrorModalWorker() {
  const daysToEvent = yield select(eventsSelectors.currentEventDaysAway)
  const errorObj = yield select(addToLookSelectors.errorToDisplay)
  yield call(addToLookTrack.viewedAtlBlockModal, daysToEvent, get(errorObj, 'type'))
}

/**
 * Handles the activation of the error modal on the PDP
 *
 * @param {String} origin Where look creation was initiated from
 * @param {Array} error Array of AddToLook blocking errors coming from the backend
 * @generator
 */
export function* activatePdpErrorModalWorker(origin, errors, status) {
  yield put(addToLookActions.addToLookFailure(errors))

  // Conditions to control PDP specific AddToLook errors to surface on PDP.
  if ([ATL_ORIGIN_HTO, ATL_ORIGIN_PDP].includes(origin) && status === BAD_REQUEST) {
    yield put(userInteractionsActions.openPdpErrorModal())
    yield call(trackErrorModalWorker)
  } else {
    yield put(userInteractionsActions.openErrorModal())
  }
}

/**
 * Handles the creation of an event look for the user's current event.
 *
 * @param {Object} action
 * @param {Object} action.payload
 * @param {String} action.payload.lookName The name of the look to create
 * @param {Boolean} [action.payload.isHto] Whether this should be a HTO look or not
 * @param {String} action.payload.origin The initiator of look creation
 * @generator
 */
export function* createEventLookWorker({payload: {lookName, isHto = false, origin}}) {
  const eventId = yield select(eventsSelectors.currentId)
  yield put(apiActions.createEventLookPending())
  try {
    const response = yield call(eventAndLooksApi.createEventLook, eventId, {
      look_name: lookName,
      is_hto: isHto,
    })

    if (isBadResponse(response.status)) {
      const responseData = get(response, 'json', {})
      yield call(activatePdpErrorModalWorker, origin, get(responseData, 'errors'), response.status)
      throw new Error(responseData)
    }

    yield put(apiActions.createEventLookSuccess(get(response, 'json', {})))
    yield call(looksTrack.createdLook, lookName, origin)
  } catch {
    yield put(apiActions.createEventLookFailure('Could not create look.'))
  }
}

export function* createEventLookSaga() {
  yield takeLatest(apiActions.CREATE_EVENT_LOOK, createEventLookWorker)
}
